// src/pages/MatchSetup/SidesGrid.js
//
// Two side cards (A, B). Each shows:
//   - team name + count (5/11)
//   - captain row
//   - aggregated strength bars
//   - composition counter (role-by-tier breakdown of the squad so far)
//   - squad list (captain first, then by pick_order)
//
// Each squad chip uses the same Option-A layout as UnassignedList:
//   [Pick #] [Name] [Caps]                       [×]
//   Role · Tier N · Bat T# Bowl T# · F# · Captain · ...

import React from 'react';
import { CapBadge, makeCapBadgeMap } from './Caps';

function deriveRole(strength) {
  if (!strength) return 'BAT';
  const bat  = Number(strength.batting_pts) || 0;
  const bowl = Number(strength.bowling_pts) || 0;
  if (bat <= 0 && bowl <= 0) return 'BAT';
  const total = bat + bowl;
  const batShare  = total ? bat  / total : 1;
  const bowlShare = total ? bowl / total : 0;
  if (batShare >= 0.7) return 'BAT';
  if (bowlShare >= 0.7) return 'BOWL';
  return 'AR';
}

function isKeeperBySeason(strength) {
  if (!strength) return false;
  const kInn = Number(strength.keeper_innings) || 0;
  if (kInn < 3) return false;
  const att = Math.max(
    Number(strength.matches_attended) || 0,
    Number(strength.batting_innings)  || 0,
    Number(strength.bowling_innings)  || 0,
    kInn,
  );
  return att > 0 && (kInn / att) >= 0.25;
}

function roleLabel(role) {
  if (role === 'BOWL') return 'Bowler';
  if (role === 'AR')   return 'All-rounder';
  return 'Batter';
}

function buildCaptionParts({ m, role, strength, priority, isCaptain, isKeeper, isSaanjha }) {
  const parts = [];

  // Role(s) — stacked. Primary BAT/BOWL/AR first, then "Keeper" if applicable.
  // A pure keeper with no batting/bowling history shows just "Keeper".
  const isSeasonKeeper = isKeeperBySeason(strength);
  const hasBatBowlHistory =
    (Number(strength?.batting_innings) || 0) > 0 ||
    (Number(strength?.bowling_innings) || 0) > 0;
  if (hasBatBowlHistory) {
    parts.push({ key: 'role', text: roleLabel(role) });
    if (isSeasonKeeper) {
      parts.push({ key: 'role-wk', text: 'Keeper', className: 'ms-cap-keeper' });
    }
  } else if (isSeasonKeeper) {
    parts.push({ key: 'role', text: 'Keeper', className: 'ms-cap-keeper' });
  } else {
    parts.push({ key: 'role', text: roleLabel(role) });
  }

  if (priority) {
    parts.push({
      key: 'floater',
      text: priority.label.toUpperCase(),
      className: 'ms-cap-floater',
      title: priority.tier === 1
        ? 'Floater priority F1 — never floated this season'
        : `Floater priority — ${priority.elapsed_weeks} week${priority.elapsed_weeks === 1 ? '' : 's'} since last float`,
    });
  }

  if (m?.ucc_id && m.ucc_id.startsWith('GST')) {
    parts.push({ key: 'guest', text: 'Guest', className: 'ms-cap-guest' });
  }
  if (isCaptain) parts.push({ key: 'cap', text: 'Captain', className: 'ms-cap-captain' });
  // "Keeper today" only when they're keeping THIS match but aren't a regular
  // season keeper — avoids duplicating the Keeper pill above for season keepers
  // who are also keeping today (the common case).
  if (isKeeper && !isSeasonKeeper) {
    parts.push({ key: 'wk-today', text: 'Keeper today', className: 'ms-cap-keeper' });
  }
  if (isSaanjha) parts.push({ key: 'sb', text: 'Saanjha' });

  if (m?.no_bowling)    parts.push({ key: 'nb', text: 'No bowling', className: 'ms-cap-warn' });
  if (m?.no_keeping)    parts.push({ key: 'nk', text: 'No keeping', className: 'ms-cap-warn' });
  if (m?.leaving_early) {
    const t = m.leave_time ? String(m.leave_time).slice(0, 5) : 'early';
    parts.push({ key: 'le', text: `Leaving ${t}`, className: 'ms-cap-warn' });
  }

  // Per-discipline ranks — separate list so the renderer can place them on
  // a second line below the role line. Fixed order Bat → Bowl → AR → Keep → Fld.
  const rankPills = [];
  if (strength) {
    const order = [
      { key: 'rank-bat',  rank: strength.batting_rank,  prefix: 'Bat',  title: 'Batter rank by total runs this season (today\'s playing pool)' },
      { key: 'rank-bowl', rank: strength.bowling_rank,  prefix: 'Bowl', title: 'Bowler rank by total wickets this season' },
      { key: 'rank-ar',   rank: strength.ar_rank,       prefix: 'AR',   title: 'All-rounder rank by total UCC points this season' },
      { key: 'rank-keep', rank: strength.keeper_rank,   prefix: 'Keep', title: 'Keeper rank by stumpings + caught-behind this season' },
      { key: 'rank-fld',  rank: strength.fielding_rank, prefix: 'Fld',  title: 'Outfield-fielder rank by catches + run-outs this season' },
    ];
    for (const p of order) {
      if (!p.rank) continue;
      rankPills.push({
        key: p.key,
        text: `${p.prefix} #${p.rank}`,
        isTop3: p.rank <= 3,
        title: p.title,
      });
    }
  }

  return { parts, rankPills };
}

function CaptionLine({ parts }) {
  if (!parts || parts.length === 0) return null;
  const out = [];
  parts.forEach((p, i) => {
    if (i > 0) out.push(<span key={`s${i}`} className="ms-cap-sep">·</span>);
    out.push(<span key={p.key} className={p.className || ''} title={p.title || ''}>{p.text}</span>);
  });
  return <div className="ms-caption">{out}</div>;
}

function RankLine({ pills }) {
  if (!pills || pills.length === 0) return null;
  return (
    <div className="ms-rank-line">
      {pills.map(p => (
        <span
          key={p.key}
          className={p.isTop3 ? 'ms-rank-pill ms-rank-pill-top' : 'ms-rank-pill'}
          title={p.title}
        >
          {p.text}
        </span>
      ))}
    </div>
  );
}


// ─── StrengthBars: aggregated tier counts per discipline ─────
function StrengthBars({ rows, strengthsByMember }) {
  const c = { bat: [0,0,0], bowl: [0,0,0], fld: [0,0,0] };
  rows.forEach(r => {
    const s = strengthsByMember.get(r.member_id);
    if (!s) return;
    c.bat [(s.batting_tier  || 2) - 1]++;
    c.bowl[(s.bowling_tier  || 2) - 1]++;
    c.fld [(s.fielding_tier || 2) - 1]++;
  });

  const Row = ({ label, counts }) => {
    const total = counts[0] + counts[1] + counts[2];
    const pct = i => total ? (counts[i] / total) * 100 : 0;
    return (
      <div className="ms-sb-row">
        <div className="ms-lbl">{label}</div>
        <div className="ms-bar">
          <div className="ms-strong"     style={{ width: `${pct(0)}%` }} />
          <div className="ms-standard"   style={{ width: `${pct(1)}%` }} />
          <div className="ms-developing" style={{ width: `${pct(2)}%` }} />
        </div>
        <div className="ms-counts">{counts[0]}·{counts[1]}·{counts[2]}</div>
      </div>
    );
  };

  return (
    <div className="ms-strength-bars">
      <Row label="BAT"  counts={c.bat} />
      <Row label="BOWL" counts={c.bowl} />
      <Row label="FLD"  counts={c.fld} />
    </div>
  );
}


// ─── Composition: per-role tier breakdown of the squad picked so far ─
//
// Builds rows like:
//   BAT   [T1] [T1] [T2]
//   BOWL  [T2] [T3]
//   AR    [T1]
//   WK    [T2]   ← shown only if any keeper marked
//
// Each pip is a small chip, sized by tier (T1 large, T2 medium, T3 small).
// "Role" here is the same derivation used in captions (BAT/BOWL/AR), and WK
// is sourced from match_squads.is_keeper for this match (set by captain at
// pick time). A keeper who is also AR appears in BOTH rows — once as their
// playing role, once as the keeper.
function Composition({ rows, strengthsByMember }) {
  // Buckets: role -> array of tier numbers (1|2|3|null)
  const buckets = { BAT: [], BOWL: [], AR: [], WK: [] };

  rows.forEach(r => {
    const s = strengthsByMember.get(r.member_id);
    const role = deriveRole(s);
    const tier = s?.overall_tier || null;
    if (role === 'BAT' || role === 'BOWL' || role === 'AR') {
      buckets[role].push(tier);
    }
    if (r.is_keeper) {
      buckets.WK.push(tier);
    }
  });

  // Sort each bucket so T1 chips render leftmost.
  Object.keys(buckets).forEach(k => {
    buckets[k].sort((a, b) => (a ?? 99) - (b ?? 99));
  });

  const ROLE_META = {
    BAT:  { label: 'BAT',  className: 'ms-comp-role-bat'  },
    BOWL: { label: 'BOWL', className: 'ms-comp-role-bowl' },
    AR:   { label: 'AR',   className: 'ms-comp-role-ar'   },
    WK:   { label: 'WK',   className: 'ms-comp-role-wk'   },
  };

  // Skip empty rows entirely; show "—" if the whole squad is empty.
  const visibleRoles = ['BAT', 'BOWL', 'AR', 'WK'].filter(k => buckets[k].length > 0);

  if (rows.length === 0) {
    return (
      <div className="ms-composition ms-comp-empty">
        Composition will appear as players are picked
      </div>
    );
  }

  return (
    <div className="ms-composition">
      <div className="ms-comp-title">Composition</div>
      {visibleRoles.map(roleKey => {
        const meta = ROLE_META[roleKey];
        const tiers = buckets[roleKey];
        return (
          <div className="ms-comp-row" key={roleKey}>
            <span className={`ms-comp-role ${meta.className}`}>
              {meta.label}
              <span className="ms-comp-role-count">×{tiers.length}</span>
            </span>
            <span className="ms-comp-pips">
              {tiers.map((t, i) => {
                const cls = t === 1 ? 'ms-comp-pip ms-pip-t1'
                          : t === 2 ? 'ms-comp-pip ms-pip-t2'
                          : t === 3 ? 'ms-comp-pip ms-pip-t3'
                          :           'ms-comp-pip ms-pip-tu';
                const label = t ? `T${t}` : '—';
                const title = t
                  ? `Tier ${t} (overall season ranking)`
                  : 'No season data — likely a guest or new member';
                return (
                  <span className={cls} key={i} title={title}>{label}</span>
                );
              })}
            </span>
          </div>
        );
      })}
    </div>
  );
}


// ─── PlayerChip ──────────────────────────────────────────────
function PlayerChip({ row, member, strength, canRemove, onRemove, onSetKeeper, onEditConditions, floaterPriority, caps }) {
  const role = deriveRole(strength);
  const { parts, rankPills } = buildCaptionParts({
    m: member,
    role,
    strength,
    priority: floaterPriority,
    isCaptain: row.is_captain,
    isKeeper:  row.is_keeper,
    isSaanjha: row.is_saanjha,
  });

  return (
    <div className="ms-player-chip">
      {!row.is_captain && row.pick_order != null && (
        <span className="ms-pick-num">#{row.pick_order}</span>
      )}
      <div
        className="ms-name-stack"
        onClick={() => onEditConditions && member && onEditConditions(member)}
        style={{ cursor: onEditConditions ? 'pointer' : 'default' }}
        title={onEditConditions ? 'Tap to edit conditions' : ''}
      >
        <div className="ms-name-row">
          <span className="ms-pname">{member?.display_name || row.member_id}</span>
          {(caps || []).map(cap => <CapBadge key={cap} kind={cap} />)}
        </div>
        <CaptionLine parts={parts} />
        <RankLine pills={rankPills} />
      </div>
      {canRemove && onSetKeeper && (
        <button
          className="ms-icon-btn-sm"
          onClick={() => onSetKeeper(row.member_id, !row.is_keeper)}
          disabled={!!member?.no_keeping && !row.is_keeper}
          title={member?.no_keeping
            ? "Member has asked not to keep wickets"
            : (row.is_keeper ? 'Unmark wicketkeeper' : 'Mark as wicketkeeper')}
          style={{
            marginRight: 4,
            filter: row.is_keeper ? 'none' : 'grayscale(1)',
            opacity: (member?.no_keeping && !row.is_keeper) ? 0.3 : (row.is_keeper ? 1 : 0.55),
            border: row.is_keeper ? '1.5px solid #F5C518' : '1px solid transparent',
            borderRadius: 6,
          }}
        >🧤</button>
      )}
      {canRemove && !row.is_captain && (
        <button className="ms-icon-btn-sm" onClick={() => onRemove(row.member_id)} title="Send back to unassigned">×</button>
      )}
    </div>
  );
}


// ─── SideCard ────────────────────────────────────────────────
function SideCard({
  side,
  squadRows,
  presentByMemberId,
  strengthsByMember,
  squadSize,
  isOnClock,
  canRemove,
  onRemove,
  onSetKeeper,
  teamName,
  onTeamNameChange,
  canEditTeamName,
  onEditConditions,
  floaterPriorities,
  capBadgeMap,
}) {
  const sideClass = side === 'A' ? 'ms-side-a' : 'ms-side-b';
  const captainRow = squadRows.find(r => r.is_captain);
  const captainPresent = captainRow ? presentByMemberId.get(captainRow.member_id) : null;

  const sorted = [...squadRows].sort((a, b) => {
    if (a.is_captain && !b.is_captain) return -1;
    if (b.is_captain && !a.is_captain) return  1;
    return (a.pick_order || 0) - (b.pick_order || 0);
  });

  return (
    <div className={`ms-side-card ${sideClass} ${isOnClock ? 'ms-on-clock' : ''}`}>
      <div className="ms-side-head">
        <div className="ms-badge-big">{side}</div>
        <input
          type="text"
          className="ms-team-input"
          value={teamName || ''}
          onChange={e => onTeamNameChange(e.target.value)}
          disabled={!canEditTeamName}
          placeholder={`Side ${side}`}
        />
        <div className="ms-side-count">{squadRows.length}/{squadSize}</div>
      </div>

      {captainRow && (
        <div className="ms-captain-line">
          <span className="ms-c-pill">CAP</span>
          {captainPresent?.display_name || captainRow.member_id}
          <span style={{ color: 'var(--ms-gray-500)', marginLeft: 'auto', fontSize: 10 }}>from wheel</span>
        </div>
      )}

      <StrengthBars rows={squadRows} strengthsByMember={strengthsByMember} />

      <div className={`ms-squad-list ${squadRows.length === 0 ? 'ms-empty' : ''}`}>
        {squadRows.length === 0
          ? 'No players yet'
          : sorted.map(r => (
              <PlayerChip
                key={r.member_id}
                row={r}
                member={presentByMemberId.get(r.member_id)}
                strength={strengthsByMember.get(r.member_id)}
                canRemove={canRemove}
                onRemove={onRemove}
                onSetKeeper={onSetKeeper}
                onEditConditions={onEditConditions}
                floaterPriority={floaterPriorities && floaterPriorities[r.member_id]}
                caps={capBadgeMap.get(r.member_id) || []}
              />
            ))}
      </div>
    </div>
  );
}


// ─── Top-level export ────────────────────────────────────────
export default function SidesGrid({
  squad,
  captaincy,
  presentMembers,
  strengthsByMember,
  squadSize,
  turn,
  canEditMeta,
  isAdmin,
  myCaptainSide,
  teamAName, teamBName,
  onTeamAChange, onTeamBChange,
  onRemove,
  onSetKeeper,
  onEditConditions,
  floaterPriorities,
  capHolders,
}) {
  const presentByMemberId = new Map();
  presentMembers.forEach(p => presentByMemberId.set(p.id, p));

  const aRows = squad.filter(r => r.side === 'A');
  const bRows = squad.filter(r => r.side === 'B');

  const canRemoveA = isAdmin || myCaptainSide === 'A';
  const canRemoveB = isAdmin || myCaptainSide === 'B';

  const capBadgeMap = makeCapBadgeMap(capHolders);

  const common = {
    presentByMemberId,
    strengthsByMember,
    squadSize,
    onRemove,
    onSetKeeper,
    onEditConditions,
    floaterPriorities,
    capBadgeMap,
  };

  return (
    <div className="ms-sides-grid">
      <SideCard
        side="A"
        squadRows={aRows}
        isOnClock={turn === 'A'}
        canRemove={canRemoveA}
        canEditTeamName={isAdmin || myCaptainSide === 'A'}
        teamName={teamAName}
        onTeamNameChange={onTeamAChange}
        {...common}
      />
      <SideCard
        side="B"
        squadRows={bRows}
        isOnClock={turn === 'B'}
        canRemove={canRemoveB}
        canEditTeamName={isAdmin || myCaptainSide === 'B'}
        teamName={teamBName}
        onTeamNameChange={onTeamBChange}
        {...common}
      />
    </div>
  );
}
